/**
 * F5 刷新恢复（F3 阶段 4，§8 方案 A）：sessionStorage 按标签页暂存浮窗里的会话，刷新后接回同一个 conversationId。
 *
 * - 为什么 sessionStorage：按标签页隔离、关标签即清、与「关闭 = 结束会话」一致（§8.2）
 * - 键带 userId（`ai-chat:session:<userId>`），读写都 try/catch；非 JSON / 版本不符 / 形状不对 → 丢弃、删掉坏记录、打诊断
 * - 只存可渲染的消息；没有消息的会话不存，一个都没有就删键（不留空记录）
 * - 超过 `SNAPSHOT_MAX_CHARS` 从最早的消息开始丢，并标 truncated（列表顶部提示「更早的消息未保存」）
 * - 前端不做任何过期判断：会话不会因 24h 过期（§5.1），后端拒绝了就如实显示
 */
import { isTextMessage, type ChatMessage, type ChatTurn } from './types'
import { CHAT_SNAPSHOT_KEY_PREFIX } from './chatSessionClear'

const VERSION = 1
/** sessionStorage 一般 5MB（UTF-16），留足余量 */
export const SNAPSHOT_MAX_CHARS = 1_000_000
/** 前端专用的确认失败码：点了 Confirm、结果还没回来页面就刷新了——写没写进去不知道（§8.3） */
export const CLIENT_INTERRUPTED = 'CLIENT_INTERRUPTED'

export interface StoredSession {
  id: string
  entry: string
  /** label 只做显示（刷新后标签 / 上下文头还要显示），不上线 */
  subject?: { type: string; id: string; label?: string }
  /** = entry_context.intent_hint，刷新后照样每轮透传 */
  hint?: string
  /** 页面专用助手绑着的页面（路径前缀）；刷新后离开那个页面照样换回通用助手 */
  page?: string
  conversationId: string
  messages: ChatMessage[]
  /** 保存时这一轮流还在跑（刷新会把它打断） */
  inFlight: boolean
  /** 那一轮是怎么起的（决定恢复后能不能重新发送）；老快照没有 = 不给重新发送 */
  turn?: ChatTurn
  truncated?: boolean
}

export interface SessionSnapshot {
  windowState: 'open' | 'minimized'
  activeId: string | null
  sessions: StoredSession[]
}

export const sessionStorageKey = (userId: string) => `${CHAT_SNAPSHOT_KEY_PREFIX}${userId}`

const warn = (msg: string, extra?: unknown) => console.warn(`[ai-chat] ${msg}`, extra ?? '')

const remove = (userId: string) => {
  try { sessionStorage.removeItem(sessionStorageKey(userId)) } catch (err) { warn('session snapshot not removable (storage blocked)', err) }
}

export function clearSnapshot(userId: string | null): void {
  if (userId) remove(userId)
}

const isSubject = (v: unknown): boolean => {
  const s = v as StoredSession['subject']
  return !!s && typeof s.type === 'string' && typeof s.id === 'string' && (s.label === undefined || typeof s.label === 'string')
}

const isSession = (v: unknown): v is StoredSession => {
  const s = v as StoredSession
  return !!s && typeof s.id === 'string' && typeof s.entry === 'string' && typeof s.conversationId === 'string'
    && Array.isArray(s.messages) && typeof s.inFlight === 'boolean'
    // 被篡改 / 损坏的快照：非字符串的 label 进渲染会让浮窗崩掉，整份丢弃
    && (s.subject === undefined || isSubject(s.subject)) && (s.hint === undefined || typeof s.hint === 'string')
    && (s.page === undefined || typeof s.page === 'string')
}

export function loadSnapshot(userId: string | null): SessionSnapshot | null {
  if (!userId) return null
  let raw: string | null
  try {
    raw = sessionStorage.getItem(sessionStorageKey(userId))
  } catch (err) {
    warn('session snapshot not readable (storage blocked), starting fresh', err)
    return null
  }
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Record<string, unknown>
    const ok = v?.version === VERSION && (v.windowState === 'open' || v.windowState === 'minimized')
      && (v.activeId === null || typeof v.activeId === 'string') && Array.isArray(v.sessions) && v.sessions.every(isSession)
    if (!ok) {
      warn('session snapshot discarded: unexpected version / shape', { version: v?.version })
      remove(userId)
      return null
    }
    return { windowState: v.windowState as SessionSnapshot['windowState'], activeId: v.activeId as string | null, sessions: v.sessions as StoredSession[] }
  } catch {
    warn('session snapshot discarded: not JSON', { length: raw.length })
    remove(userId)
    return null
  }
}

export function saveSnapshot(userId: string | null, snapshot: SessionSnapshot): void {
  if (!userId) return
  // 有消息才存；新 Agent 平台的标签页不在本地存消息（服务端有），只存会话 id——有服务端 id 也要存，否则刷新就丢了这个标签
  const sessions = snapshot.sessions.filter(s => s.messages.length > 0 || /^\d+$/.test(s.conversationId))
    .map(s => ({ ...s, messages: s.messages.slice() }))
  if (!sessions.length) { remove(userId); return }
  const encode = () => JSON.stringify({ version: VERSION, ...snapshot, sessions })
  // 超了：从消息最多的会话里丢最早的一条，直到放得下（每个会话至少留最后一条）。
  // 先按每条消息的编码长度估总长、用减法定位截点，最后只整包编码一次（review 09-14：原先每丢一条就整包 stringify）
  const lens = sessions.map(s => s.messages.map(m => JSON.stringify(m).length + 1))
  const base = JSON.stringify({ version: VERSION, ...snapshot, sessions: sessions.map(s => ({ ...s, messages: [], truncated: true })) }).length
  let total = base + lens.reduce((sum, l) => sum + l.reduce((a, b) => a + b, 0), 0)
  while (total > SNAPSHOT_MAX_CHARS) {
    let victim = -1
    for (let i = 0; i < sessions.length; i++) {
      if (sessions[i].messages.length > 1 && (victim === -1 || sessions[i].messages.length > sessions[victim].messages.length)) victim = i
    }
    if (victim === -1) break
    sessions[victim].messages.shift()
    sessions[victim].truncated = true
    total -= lens[victim].shift()!
  }
  let raw = encode()
  // 估算有偏差时的兜底（很少走到）：逐条丢到真的放得下
  while (raw.length > SNAPSHOT_MAX_CHARS) {
    const victim = sessions.filter(s => s.messages.length > 1).sort((a, b) => b.messages.length - a.messages.length)[0]
    if (!victim) {
      // 单条就超限：不能留着上一版（会恢复出过时状态），删掉
      warn('session snapshot not saved: a single message is larger than the limit; previous snapshot removed', { max: SNAPSHOT_MAX_CHARS })
      remove(userId)
      return
    }
    victim.messages.shift()
    victim.truncated = true
    raw = encode()
  }
  try {
    sessionStorage.setItem(sessionStorageKey(userId), raw)
  } catch (err) {
    // 写不进去（配额满 / 被禁用）：上一版快照已经过时（比如卡片已确认），留着会恢复出错误状态，删掉
    warn('session snapshot not saved (storage blocked / full); previous snapshot removed', err)
    remove(userId)
  }
}

/** 恢复后的消息：过期卡置 expired、确认中被刷新的卡置「结果未知」、被打断的一轮加提示、截断过的加顶部提示 */
export function restoreMessages(session: StoredSession, nowMs: number): ChatMessage[] {
  let messages: ChatMessage[] = session.messages.map(m => {
    if (m.kind !== 'confirmation' || !m.confirmation) return m
    const { status, pending } = m.confirmation
    if (status === 'submitting') {
      return { ...m, confirmation: { ...m.confirmation, status: 'failed', failureCode: CLIENT_INTERRUPTED } }
    }
    if (status === 'pending' && pending.expiresAt && Date.parse(pending.expiresAt) <= nowMs) {
      return { ...m, confirmation: { ...m.confirmation, status: 'expired' } }
    }
    return m
  })
  if (session.inFlight) {
    const last = messages.at(-1)
    if (last && last.role === 'assistant' && isTextMessage(last) && !last.content) messages = messages.slice(0, -1)
    const turn = session.turn
    // 本轮的确认卡已经到了（confirmation_required 先于 done）：卡片自己就是这一轮的结果，不再挂矛盾的「助手没记住」
    const lastUserIdx = messages.map(m => m.role === 'user').lastIndexOf(true)
    const cardArrived = messages.slice(lastUserIdx + 1).some(m => m.kind === 'confirmation' && m.confirmation?.status === 'pending')
    // 确认续流被打断：卡片已置「结果未知，去核实」；再给重新发送会重发当初的写请求，可能重复写入
    if (turn?.kind !== 'confirm' && !cardArrived) {
      // 只有普通消息能原样重发：start_flow 的气泡是按钮文字、cancel_flow 没有气泡、老快照不知道是哪种
      const resendText = turn?.kind === 'message' ? turn.text : undefined
      messages = [...messages, { role: 'assistant', content: '', kind: 'notice', notice: 'interrupted', ...(resendText ? { resendText } : {}) }]
    }
  }
  if (session.truncated) messages = [{ role: 'assistant', content: '', kind: 'notice', notice: 'truncated' }, ...messages]
  return messages
}
